import React from 'react';

import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ClickEventComponentNames } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { primitive } from 'styles/constants/primitives';

import { useCheckoutState } from '../../checkout-provider';
import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutSubmit } from '../../hooks/use-checkout-submit';
import { useCheckoutValidation } from '../../hooks/use-checkout-validation';

export const PlaceOrderButton = () => {
  const { formatMessage } = useIntl();
  const { submitOrder, ErrorDialog } = useCheckoutSubmit();
  const { validate } = useCheckoutValidation();
  const checkoutErrors = validate();
  const [, setCheckoutState] = useCheckoutState();
  const {
    isCheckoutLoading,
    isLoadingPaymentMethods,
    isOrderPriceLoading,
  } = useCheckoutLoadingState();

  // there is a isCheckoutLoading loading flicker (true->false->true) without debouncing. TODO find root cause and fix
  const debouncedCheckoutLoading = useDebouncedValue(isCheckoutLoading);
  const { hasPaymentProcessingTakeover } = useAppTakeoverCart();
  const hasFooterErrors = checkoutErrors.some(error => error.placement === 'footer');
  const { isOrderPricingRetryInProgress } = useCartContext();

  const handlePress = () => {
    if (checkoutErrors.length === 0) {
      submitOrder();
    }
    const dialogErrors = checkoutErrors.filter(error => error.placement === 'errorDialog');
    if (dialogErrors.length > 0) {
      // TODO: we may need to rethink this if we want take different actions based on error type
      setCheckoutState(prev => ({ ...prev, errorModalOpen: true }));
    }
  };

  let buttonText = formatMessage({ id: 'placeOrder' });

  if (isOrderPricingRetryInProgress) {
    buttonText = formatMessage({ id: 'thisMayTakeAFewSeconds' });
  } else if (debouncedCheckoutLoading && !isLoadingPaymentMethods && !isOrderPriceLoading) {
    buttonText = formatMessage({
      id: 'placingOrderCheckoutBtn',
      defaultMessage: 'Placing Order',
    });
  }

  return (
    <>
      {hasPaymentProcessingTakeover && debouncedCheckoutLoading && <PaymentProcessLoading />}
      <View style={styles.buttonContainer}>
        <ActionButton
          onPress={handlePress}
          disabled={debouncedCheckoutLoading || hasFooterErrors}
          size={ActionButtonSizes.LARGE}
          isLoading={debouncedCheckoutLoading}
          fullWidth
          eventAttributes={{
            component: ClickEventComponentNames.BUTTON,
            name: 'Place Secure Order',
          }}
        >
          {buttonText}
        </ActionButton>
      </View>

      {/** only allow when debouncedCheckoutLoading is false or else the PaymentProcessLoading can conflict with it (multiple modals at once can cause strange issues) */}
      {!debouncedCheckoutLoading && <ErrorDialog />}
    </>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    width: '100%',
    paddingHorizontal: 16,
    marginTop: primitive.spacing.$4,
  },
});
